import {NotifyUserHasChangedUseCase} from './notify-user-has-change.use-case';
import {Injectable} from "@angular/core";
import {firstValueFrom, from} from "rxjs";
import {ActualCostumerSignUpData} from "@app/core/user/domain/dtos/actual-costumer-sign-up-data";
import {UserRemote} from "@app/core/user/domain/user.remote";
import {take} from "rxjs/operators";
import {UserSaver} from "@app/core/user/domain/user-saver";
import {
    DeliveryPointSelector,
    SelectDeliveryPointUseCase
} from "@app/core/delivery-point/application/select-delivery-point.use-case";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {SaveDeliveryPointUseCase} from "@app/core/delivery-point/application/save-delivery-point.use-case";
import {Encrypt} from "@app/core/user/domain/encrypt";
import {
    SendDeliveryPointSelectionUseCase
} from './../../delivery-point/application/send-delivery-point-selection.use-case';
import {TrackSignUpUseCase} from "@app/core/tracking/application/track-sign-up.use-case";
import {PromoterRepo} from '@app/core/promoter/domain/promoter-repo';


export interface SignUpActualCustomerUserCasePrams {
    actualCostumerSignUpData: ActualCostumerSignUpData;
    deliveryPointSelector: DeliveryPointSelector;
}

@Injectable({
    providedIn: "root",
})
export class SignUpActualCustomerUseCase {

    constructor(
        private webService:UserRemote,
        private saveUser: UserSaver,
        private selectDeliveryPointUseCase: SelectDeliveryPointUseCase,
        private saveDeliveryPointUseCase: SaveDeliveryPointUseCase,
        private sendDeliveryPointSelectionUseCase: SendDeliveryPointSelectionUseCase,
        private notifyUserHasChanged: NotifyUserHasChangedUseCase,
        private trackSignUpUseCase: TrackSignUpUseCase,
        private promoterRepo: PromoterRepo,
    ) {}

    execute(params: SignUpActualCustomerUserCasePrams) {
        return from(this.signUpActualCustomer(params));
    }

    /**
     * It signs up an actual customer, selects a delivery point, saves the delivery point, and saves the user
     * @param {SignUpActualCustomerUserCasePrams} params - SignUpActualCustomerUserCasePrams
     */
    private async signUpActualCustomer(params: SignUpActualCustomerUserCasePrams): Promise<void> {
        const user = await firstValueFrom(this.webService.signUpActualCustomer(params.actualCostumerSignUpData).pipe(take(1)));
        // Extract parameter credentials
        const { email, password, promoter } =  params.actualCostumerSignUpData;

        // Create credentials without password hashed to use in local save
        const uncryptedCredentials = new Credentials(user.email, password);

        // Create credentials with password hashed to use in delivery enpoint
        const hashedPassword = await Encrypt.sha1(password);
        const encryptedCredentials = new Credentials(email, hashedPassword);
        const selectedDeliveryPoint = await this.selectDeliveryPointUseCase.execute({
            credentials: encryptedCredentials,
            deliveryPointSelector: params.deliveryPointSelector
        });

        if (selectedDeliveryPoint == null) {
            return;
        }

        if (promoter != null) {
            this.promoterRepo.save(promoter);
        }

        await this.sendDeliveryPointSelectionUseCase.execute(selectedDeliveryPoint.code, encryptedCredentials);
        await this.saveUser.saveWithPassword(user, uncryptedCredentials);
        await firstValueFrom(this.saveDeliveryPointUseCase.execute({deliveryPoint:selectedDeliveryPoint}).pipe(take(1)));
        this.notifyUserHasChanged.execute({email: user.email, deliveryPointCode: selectedDeliveryPoint.code})
        this.trackSignUpUseCase.execute();
    }
}
